// src/lib/objectBounds.js Pure bounding box utilities for Redux display objects. No React, no Redux imports.

import { insideByRoles, subpathRoles } from '~/lib/outline/fill'
import { stringToRawPath } from '~/lib/svg/paths'

// Evaluate one axis of a cubic bezier at t
function cubicAt(p0, p1, p2, p3, t) {
	const u = 1 - t
	return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
}

// Returns candidate axis-values at bezier extrema (does not include t=0 and t=1 endpoints)
function bezierExtrema(p0, p1, p2, p3) {
	const a = -p0 + 3 * p1 - 3 * p2 + p3
	const b = 2 * p0 - 4 * p1 + 2 * p2
	const c = -p0 + p1
	const roots = []
	if (Math.abs(a) < 1e-10) {
		if (Math.abs(b) > 1e-10) roots.push(-c / b)
	} else {
		const disc = b * b - 4 * a * c
		if (disc >= 0) {
			const sq = Math.sqrt(disc)
			roots.push((-b + sq) / (2 * a), (-b - sq) / (2 * a))
		}
	}
	return roots.filter(t => t > 0 && t < 1).map(t => cubicAt(p0, p1, p2, p3, t))
}

// Count horizontal ray crossings of a cubic bezier from (px, py) going right. Returns number of crossings (for winding/even-odd rule).
function rayCrossBezier(px, py, x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1) {
	// Quick Y reject
	const minY = Math.min(y0, cp1y, cp2y, y1)
	const maxY = Math.max(y0, cp1y, cp2y, y1)
	if (py < minY || py > maxY) return 0

	// Flatten to ~16 line segments and count crossings
	const N = 16
	let crossings = 0
	let prevX = x0,
		prevY = y0
	for (let i = 1; i <= N; i++) {
		const t = i / N
		const u = 1 - t
		const nx = u * u * u * x0 + 3 * u * u * t * cp1x + 3 * u * t * t * cp2x + t * t * t * x1
		const ny = u * u * u * y0 + 3 * u * u * t * cp1y + 3 * u * t * t * cp2y + t * t * t * y1
		// Check if horizontal ray from (px, py) crosses segment (prevX,prevY)→(nx,ny)
		if ((prevY <= py && ny > py) || (ny <= py && prevY > py)) {
			const xAt = prevX + ((py - prevY) / (ny - prevY)) * (nx - prevX)
			if (xAt > px) crossings++
		}
		prevX = nx
		prevY = ny
	}
	return crossings
}

/**
 * Point-in-path test using even-odd ray casting. Tests point (px, py) in object-local space against rawPath bezier data.
 * @param {Array} rawPath — GreenSock rawPath segments
 * @param {number} px — local x
 * @param {number} py — local y
 * @returns {boolean}
 */
export function pointInRawPath(rawPath, px, py) {
	// Check each segment independently — point is inside if it's inside ANY sub-polygon. This handles split outlines from self-intersecting brushes.
	for (const seg of rawPath) {
		let crossings = 0
		for (let i = 0; i + 7 < seg.length; i += 6) {
			crossings += rayCrossBezier(
				px,
				py,
				seg[i],
				seg[i + 1],
				seg[i + 2],
				seg[i + 3],
				seg[i + 4],
				seg[i + 5],
				seg[i + 6],
				seg[i + 7]
			)
		}
		if ((crossings & 1) === 1) return true
	}
	return false
}

/** Hit test by the shape's fill rule (`~/lib/outline/fill`): each contour even-odd, then islands fill and opposite-winding holes cut as PixiShape paints them. */
export function pointInRawPathFilled(rawPath, px, py) {
	return insideByRoles(subpathRoles(rawPath), i => pointInRawPath([rawPath[i]], px, py))
}

// Minimum squared distance from point (px, py) to a cubic bezier, sampled at N steps.
function distSqBezier(px, py, x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1) {
	const N = 16
	let minSq = Infinity
	for (let i = 0; i <= N; i++) {
		const t = i / N
		const u = 1 - t
		const bx = u * u * u * x0 + 3 * u * u * t * cp1x + 3 * u * t * t * cp2x + t * t * t * x1
		const by = u * u * u * y0 + 3 * u * u * t * cp1y + 3 * u * t * t * cp2y + t * t * t * y1
		const dSq = (px - bx) ** 2 + (py - by) ** 2
		if (dSq < minSq) minSq = dSq
	}
	return minSq
}

/**
 * Test if point (px, py) in object-local space is within `radius` of any bezier in rawPath.
 */
export function pointNearRawPath(rawPath, px, py, radius) {
	const rSq = radius * radius
	for (const seg of rawPath) {
		for (let i = 0; i + 7 < seg.length; i += 6) {
			if (
				distSqBezier(
					px,
					py,
					seg[i],
					seg[i + 1],
					seg[i + 2],
					seg[i + 3],
					seg[i + 4],
					seg[i + 5],
					seg[i + 6],
					seg[i + 7]
				) <= rSq
			) {
				return true
			}
		}
	}
	return false
}

// Compute AABB for a path stored as rawPath segments. Segment format: [x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1, cp1x, cp1y, ...] Each bezier shares its end anchor with the next start anchor (no repetition of anchors).
export function rawPathBounds(rawPath) {
	let minX = Infinity,
		minY = Infinity,
		maxX = -Infinity,
		maxY = -Infinity

	for (const seg of rawPath) {
		// Walk bezier curves: start at index 0, each curve is 6 values (after first 2 anchor)
		for (let i = 0; i + 7 < seg.length; i += 6) {
			const x0 = seg[i],
				y0 = seg[i + 1]
			const cp1x = seg[i + 2],
				cp1y = seg[i + 3]
			const cp2x = seg[i + 4],
				cp2y = seg[i + 5]
			const x1 = seg[i + 6],
				y1 = seg[i + 7]

			// X axis
			const xs = [x0, x1, ...bezierExtrema(x0, cp1x, cp2x, x1)]
			for (const v of xs) {
				if (v < minX) minX = v
				if (v > maxX) maxX = v
			}
			// Y axis
			const ys = [y0, y1, ...bezierExtrema(y0, cp1y, cp2y, y1)]
			for (const v of ys) {
				if (v < minY) minY = v
				if (v > maxY) maxY = v
			}
		}
	}

	if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 }
	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

/**
 * Local-space offset of a scaled box's near edge, honouring the SIGN of the scale.
 *
 * A negative scale mirrors the box about the origin, so the far edge becomes the near one and the whole box lands on the other side. Taking `Math.abs` here instead puts the box on the unflipped side — bounds desynced from the rendered object, which is the bug this exists to prevent. The returned extent is always positive; only the origin moves.
 *
 * @returns {[number, number]} `[offset, extent]`
 */
function signedExtent(near, size, scale) {
	const far = near + size
	return [scale < 0 ? far * scale : near * scale, size * Math.abs(scale)]
}

const RAD = Math.PI / 180
const NONE = { x: 0, y: 0, width: 0, height: 0 }

/** `map` is a plain object everywhere but the transition engine, which passes a `Map`. */
const lookup = (map, id) => (map instanceof Map ? map.get(id) : map?.[id])

/** The scale the RENDERER applies to an object's local geometry, signed. A `brush` renders at identity: its outline is rebuilt from a scaled centerline, so its resize bakes (`normalizeVectorScale`). */
export function localScale(obj) {
	switch (obj?.type) {
		case 'path':
		case 'rect':
		case 'circle':
		case 'image':
		case 'group':
			return { x: obj.scaleX ?? 1, y: obj.scaleY ?? 1 }
		default:
			return { x: 1, y: 1 }
	}
}

const pathExtent = data => {
	const local = rawPathBounds(stringToRawPath(data))
	return { local, maxX: Math.max(0, local.x + local.width), maxY: Math.max(0, local.y + local.height) }
}

/**
 * The rectangle an object's geometry spans in its OWN frame: before its scale, rotation and position. Independent of `x`/`y` — what the pivot fraction is a fraction of.
 *
 * - vector: its `data` bounds, anchored at the 0,0 minimum `resetPathOrigin` normalizes to; a brush adds its stroke half-width on every side.
 * - image: `0..width` × `0..height` — the scale multiplies that size, so the extent is in size units, not texture pixels.
 * - group: the union of its children's ROTATED footprints, in its children's frame. ⚠️ Not their unrotated boxes: a footprint is where a child draws.
 */
export function localExtent(obj, map, depth = 0) {
	if (!obj) return NONE
	switch (obj.type) {
		case 'rect':
		case 'circle': {
			if (!obj.data) return { x: 0, y: 0, width: obj.width ?? 0, height: obj.height ?? 0 }
			const { maxX, maxY } = pathExtent(obj.data)
			return { x: 0, y: 0, width: maxX, height: maxY }
		}
		case 'brush': {
			if (!obj.data) return NONE
			const { local, maxX, maxY } = pathExtent(obj.data)
			if (obj.variableWidth) {
				const minX = Math.min(0, local.x)
				const minY = Math.min(0, local.y)
				return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
			}
			const sw = brushHalfWidth(obj)
			return { x: -sw, y: -sw, width: maxX + sw * 2, height: maxY + sw * 2 }
		}
		case 'path': {
			if (!obj.data) return NONE
			const { maxX, maxY } = pathExtent(obj.data)
			return { x: 0, y: 0, width: maxX, height: maxY }
		}
		case 'group': {
			if (!obj.children?.length || !map || depth >= 16) return NONE
			let minX = Infinity,
				minY = Infinity,
				maxX = -Infinity,
				maxY = -Infinity
			for (const id of obj.children) {
				const child = lookup(map, id)
				if (!child || child === obj) continue
				for (const p of footprint(child, map, depth + 1)) {
					if (p.x < minX) minX = p.x
					if (p.x > maxX) maxX = p.x
					if (p.y < minY) minY = p.y
					if (p.y > maxY) maxY = p.y
				}
			}
			return Number.isFinite(minX) ? { x: minX, y: minY, width: maxX - minX, height: maxY - minY } : NONE
		}
		default:
			return { x: 0, y: 0, width: obj.width ?? 0, height: obj.height ?? 0 }
	}
}

const brushHalfWidth = obj => ((obj.minWidth || 0) + (obj.widthGrowth ?? obj.maxWidth ?? 8)) / 2

/**
 * The pivot in the object's own unscaled frame — the local point `x`/`y` name. `pivotX`/`pivotY` are fractions of `localExtent`.
 *
 * ⚠️ A GROUP's pivot is its local origin, always: its children are stored relative to it (`originToPivot`), so a child edit that grows the extent never moves the siblings. A group's `pivotX`/`pivotY` are not read.
 */
export function localPivot(obj, map, extent) {
	if (!obj || obj.type === 'group') return { x: 0, y: 0 }
	const e = extent ?? localExtent(obj, map)
	return { x: e.x + (obj.pivotX ?? 0.5) * e.width, y: e.y + (obj.pivotY ?? 0.5) * e.height }
}

/** An object's position in its parent's frame. */
export function objectPosition(obj) {
	return { x: obj?.x ?? 0, y: obj?.y ?? 0 }
}

/** A local point → the object's parent frame: `x + R(θ)·S·(p − pivot)`. The one statement of the pivot model. */
export function localToParent(obj, map, p, pivot = localPivot(obj, map)) {
	const s = localScale(obj)
	const r = (obj?.rotation ?? 0) * RAD
	const cos = Math.cos(r),
		sin = Math.sin(r)
	const lx = (p.x - pivot.x) * s.x
	const ly = (p.y - pivot.y) * s.y
	const pos = objectPosition(obj)
	return { x: pos.x + lx * cos - ly * sin, y: pos.y + lx * sin + ly * cos }
}

/** The inverse of `localToParent`: a point in the parent's frame → the object's own unscaled frame. Null when a zero scale collapses an axis. */
export function parentToLocal(obj, map, q, pivot = localPivot(obj, map)) {
	const s = localScale(obj)
	if (!s.x || !s.y) return null
	const r = -(obj?.rotation ?? 0) * RAD
	const cos = Math.cos(r),
		sin = Math.sin(r)
	const pos = objectPosition(obj)
	const dx = q.x - pos.x,
		dy = q.y - pos.y
	return { x: pivot.x + (dx * cos - dy * sin) / s.x, y: pivot.y + (dx * sin + dy * cos) / s.y }
}

/** Where an object's local (0,0) lands in its parent's frame — the data origin `resetPathOrigin` normalizes to. For code that computes geometry about that origin; convert back with `positionFromOrigin`. */
export function originOf(obj, map) {
	return localToParent(obj, map, { x: 0, y: 0 })
}

/**
 * The stored position for an object whose local (0,0) should land at `origin`: `origin + R(θ)·S·pivot`, with the pivot, rotation and scale read from `obj` — pass the object as it will be AFTER the write (new `data`, `rotation`).
 *
 * Translating `data` alone never needs this: the pivot is a fraction of the extent, so it travels with the data and the picture stays put. A write that changes the extent's SIZE, or that computed a new origin, does.
 */
export function positionFromOrigin(obj, origin, map) {
	const p = localPivot(obj, map)
	const s = localScale(obj)
	const r = (obj?.rotation ?? 0) * RAD
	const cos = Math.cos(r),
		sin = Math.sin(r)
	const lx = p.x * s.x,
		ly = p.y * s.y
	return { x: origin.x + lx * cos - ly * sin, y: origin.y + lx * sin + ly * cos }
}

/** The four corners of an object's extent in its parent's frame, rotation included. Order: TL, TR, BR, BL of the local extent. */
export function footprint(obj, map, depth = 0) {
	const e = localExtent(obj, map, depth)
	const pivot = localPivot(obj, map, e)
	return [
		{ x: e.x, y: e.y },
		{ x: e.x + e.width, y: e.y },
		{ x: e.x + e.width, y: e.y + e.height },
		{ x: e.x, y: e.y + e.height },
	].map(p => localToParent(obj, map, p, pivot))
}

/**
 * The rectangle an object occupies — unsigned, without a per-type branch at the call site. Named so consumers (including imagine_srv) implement one rule rather than re-deriving the type table.
 *
 * ⚠️ UNSIGNED. The mirror does not change which rectangle is occupied — ask `renderedMirror`.
 */
/** Types whose `width`/`height` are `scale` over their extent, never stored: a group over its children, a path-like vector over its `data`. */
export const SCALE_SIZED = new Set(['group', 'path', 'rect', 'circle'])

/** A scale-sized object's size: its extent × |scale|. Null for any other type, or an empty extent. */
export function derivedSize(obj, map) {
	if (!SCALE_SIZED.has(obj?.type)) return null
	if (obj.type !== 'group' && !obj.data) return null
	const e = localExtent(obj, map)
	if (!e.width && !e.height) return null
	return { width: e.width * Math.abs(obj.scaleX ?? 1), height: e.height * Math.abs(obj.scaleY ?? 1) }
}

export function renderedSize(obj, objectsMap) {
	const bounds = getObjectBounds(obj, objectsMap)
	return { width: bounds.width, height: bounds.height }
}

/** Is this object mirrored on each axis? The mirror is the scale's SIGN; a brush bakes it into `data` and so always answers false. */
export function renderedMirror(obj) {
	if (!obj) return { x: false, y: false }
	return { x: (obj.scaleX ?? 1) < 0, y: (obj.scaleY ?? 1) < 0 }
}

/**
 * The UNROTATED box an object occupies in its parent's frame: `x + S·(extent − pivot)`. Rotation is about `x`/`y` — the pivot — so `hitTestObject` and the transformer turn this box about the object's position, not about its corner.
 *
 * One rule for every type. A negative scale mirrors about the pivot, so the box moves by `(1 − 2a)·w` — zero at the centred pivot, where a mirror is in place for scale-realized and geometry-owning types alike.
 */
export function getObjectBounds(obj, objectsMap) {
	if (!obj) return NONE
	const e = localExtent(obj, objectsMap)
	const p = localPivot(obj, objectsMap, e)
	const s = localScale(obj)
	const pos = objectPosition(obj)
	const [ox, w] = signedExtent(e.x - p.x, e.width, s.x)
	const [oy, h] = signedExtent(e.y - p.y, e.height, s.y)
	return { x: pos.x + ox, y: pos.y + oy, width: w, height: h }
}

/** A group's children's unscaled union in its own frame — the quantity a width edit divides by to write a scale. */
export function getGroupChildrenBbox(group, objectsMap) {
	if (group?.type !== 'group') return NONE
	return localExtent(group, objectsMap)
}

/** `getObjectBounds` from stored geometry alone. Kept as a name for its callers; the bounds no longer read a live pixiObj. */
export function getStoredObjectBounds(obj, objectsMap) {
	return getObjectBounds(obj, objectsMap)
}

/**
 * World-space rectangle a GROUP CHILD renders in, from stored geometry, ignoring the group's rotation. `getStoredObjectBounds` answers in group-local space, whose origin is the group's `x`/`y`; a mirrored group also moves its children's near edge to the far side.
 */
export function getStoredChildWorldBounds(child, objectsMap) {
	const local = getStoredObjectBounds(child, objectsMap)
	const group = objectsMap?.[child?.parentId]
	if (group?.type !== 'group') return local
	const [ox, w] = signedExtent(local.x, local.width, group.scaleX ?? 1)
	const [oy, h] = signedExtent(local.y, local.height, group.scaleY ?? 1)
	return { x: (group.x ?? 0) + ox, y: (group.y ?? 0) + oy, width: w, height: h }
}

/** The one stage selection rule: a locked or hidden object is not picked from the stage; everything else is, pinned included. Layers still selects either. */
export const stageSelectable = obj => !!obj && !obj.locked && obj.visible !== false

/** Locked itself or through a locked group above it: its geometry takes no edit. */
export const isLocked = (obj, objectsMap) => {
	for (let o = obj, depth = 0; o && depth < 32; o = objectsMap?.[o.parentId], depth++) if (o.locked) return true
	return false
}

export function hitTestObject(obj, pt, objectsMap) {
	if (!obj) return false
	const e = localExtent(obj, objectsMap)
	if (e.width <= 0 || e.height <= 0) return false
	const q = parentToLocal(obj, objectsMap, pt, localPivot(obj, objectsMap, e))
	if (!q || q.x < e.x || q.x > e.x + e.width || q.y < e.y || q.y > e.y + e.height) return false
	if (obj.type === 'brush' && obj.data) {
		const rawPath = stringToRawPath(obj.data)
		if (obj.variableWidth) return pointInRawPath(rawPath, q.x, q.y)
		const sw = brushHalfWidth(obj)
		return pointNearRawPath(rawPath, q.x, q.y, sw)
	}
	if ((obj.type === 'rect' || obj.type === 'circle' || obj.type === 'path') && obj.data)
		return pointInRawPathFilled(stringToRawPath(obj.data), q.x, q.y)
	// Group: hit only if a child is hit. `q` is already in the children's frame.
	if (obj.type === 'group') {
		if (!obj.children?.length || !objectsMap) return false
		return obj.children.some(id => {
			const child = lookup(objectsMap, id)
			return !!child && child !== obj && hitTestObject(child, q, objectsMap)
		})
	}
	return true
}

/** Resolve the parent group for an object: explicit editingGroup, or implicit via parentId. */
export function getParentGroup(obj, editingGroup, objectsMap) {
	if (editingGroup) return editingGroup
	if (obj?.parentId && obj.parentId !== 'root') return objectsMap[obj.parentId] ?? null
	return null
}

/** Accumulate transforms from all ancestor groups up to root (or editingGroup). */
export function getAncestorTransform(obj, editingGroup, objectsMap) {
	let x = 0,
		y = 0,
		rotation = 0,
		scaleX = 1,
		scaleY = 1
	const editingId = editingGroup?.id
	let parentId = obj?.parentId
	while (parentId && parentId !== 'root' && parentId !== editingId) {
		const parent = objectsMap[parentId]
		if (!parent) break
		const px = parent.x ?? 0,
			py = parent.y ?? 0
		const pr = ((parent.rotation ?? 0) * Math.PI) / 180
		const psx = parent.scaleX ?? 1,
			psy = parent.scaleY ?? 1
		// Apply parent transform to accumulated position
		const cos = Math.cos(pr),
			sin = Math.sin(pr)
		const nx = px + (x * psx * cos - y * psy * sin)
		const ny = py + (x * psx * sin + y * psy * cos)
		x = nx
		y = ny
		scaleX *= psx
		scaleY *= psy
		rotation += parent.rotation ?? 0
		parentId = parent.parentId
	}
	return { x, y, rotation, scaleX, scaleY }
}

/**
 * Compose a child's group-local transform with its group's transform → the child's WORLD transform. See context/geometry.md §Composition. `child`/`group` are Redux object descriptors. Returns native-space values: { x, y, scaleX, scaleY, rotation } (rotation in DEGREES).
 *
 * ⚠️ The world scale carries the SIGN of the group scale (a negative group scale mirrors the child — the sign IS the mirror). No flip-origin offset here: a signed scale pivots on the object's own origin, matching how a group Container renders the child. (The offset that lives in unGroupObjects exists only because that path bakes the mirror into geometry instead — a different flip mechanism. Mixing them double-corrects. context/geometry.md §Flips.)
 */
export function composeChildWorldTransform(child, group) {
	const sx = group.scaleX ?? 1
	const sy = group.scaleY ?? 1
	const rot = ((group.rotation ?? 0) * Math.PI) / 180
	const gCos = Math.cos(rot),
		gSin = Math.sin(rot)
	const x1 = child.x || 0
	const y1 = child.y || 0
	const x2 = x1 * sx * gCos - y1 * sy * gSin
	const y2 = x1 * sx * gSin + y1 * sy * gCos
	return {
		x: x2 + (group.x ?? 0),
		y: y2 + (group.y ?? 0),
		scaleX: (child.scaleX ?? 1) * sx,
		scaleY: (child.scaleY ?? 1) * sy,
		rotation: (child.rotation ?? 0) + (group.rotation ?? 0),
	}
}

/**
 * Resolve an object's WORLD transform against a state map by composing every group ancestor, innermost first. `map` is a Map<id, object>. Returns { x, y, scaleX, scaleY, rotation } (rotation in DEGREES).
 *
 * Walks the FULL chain: an SVG import nests groups freely, and stopping at one level leaves anything deeper expressed in its parent's local frame. Depth-capped and cycle-guarded because `parentId` comes from persisted preset data.
 */
export function resolveWorldTransform(obj, map) {
	let acc = {
		x: obj.x ?? 0,
		y: obj.y ?? 0,
		scaleX: obj.scaleX ?? 1,
		scaleY: obj.scaleY ?? 1,
		rotation: obj.rotation ?? 0,
	}
	const seen = new Set([obj.id])
	let parent = map.get(obj.parentId)
	for (let depth = 0; depth < 16 && parent?.type === 'group'; depth++) {
		if (parent.id !== undefined && seen.has(parent.id)) break
		if (parent.id !== undefined) seen.add(parent.id)
		acc = composeChildWorldTransform(acc, parent)
		parent = map.get(parent.parentId)
	}
	return acc
}

export function worldToLocal(pt, group, objectsMap) {
	const anc = getAncestorTransform(group, null, objectsMap)
	const gx = group.x ?? 0,
		gy = group.y ?? 0
	const gr = group.rotation ?? 0
	const gsx = group.scaleX ?? 1,
		gsy = group.scaleY ?? 1
	const pr = (anc.rotation * Math.PI) / 180
	const pc = Math.cos(pr),
		ps = Math.sin(pr)
	const wx = anc.x + gx * anc.scaleX * pc - gy * anc.scaleY * ps
	const wy = anc.y + gx * anc.scaleX * ps + gy * anc.scaleY * pc
	const wr = ((anc.rotation + gr) * Math.PI) / 180
	const wsx = anc.scaleX * gsx,
		wsy = anc.scaleY * gsy
	const dx = pt.x - wx,
		dy = pt.y - wy
	const cos = Math.cos(-wr),
		sin = Math.sin(-wr)
	return { x: (dx * cos - dy * sin) / wsx, y: (dx * sin + dy * cos) / wsy }
}

/**
 * The 4 corners of an unrotated bounds rect turned by `rotationRad` about `pivot` — the object's `x`/`y`. Order: [TL, TR, BR, BL].
 * @param {{ x, y, width, height }} bounds
 * @param {number} rotationRad
 * @param {{ x, y }} pivot
 */
export function getRotatedCorners(bounds, rotationRad, pivot) {
	const { x, y, width, height } = bounds
	const cos = Math.cos(rotationRad)
	const sin = Math.sin(rotationRad)
	const rotate = (px, py) => {
		const dx = px - pivot.x,
			dy = py - pivot.y
		return { x: pivot.x + dx * cos - dy * sin, y: pivot.y + dx * sin + dy * cos }
	}
	return [rotate(x, y), rotate(x + width, y), rotate(x + width, y + height), rotate(x, y + height)]
}
